import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Select } from '@tempo-io/sui-select';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TIMELINE_ITEM_IMPUTED_FIELD } from '@/common/api/types/planner/timelineItem';
import type { Team } from '@/common/api/types/teams';
import { ImputedBadge } from '@/features/items/components/data-table/cells/ImputedBadge';
import { buildGroupedTeamOptions } from '@/features/teams/utils/buildGroupedTeamOptions';
import {
  mapTeamToSelectOption,
  type TeamSelectOption,
} from '@/features/teams/utils/mapTeamToSelectOption';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';

export const TeamEditCell = ({
  teamsById,
  loopId,
  id,
  row,
  field,
  api,
}: GridRenderEditCellParams<ItemsTableRow> & {
  teamsById: Record<Team['id'], Team>;
  loopId?: number;
}) => {
  const cellId = `team-assignment-${id}-${field}`;
  const { t } = useTranslation();
  const itemId = Number(id);

  const teamAssignmentOptions = React.useMemo(
    () =>
      buildGroupedTeamOptions(Object.values(teamsById), loopId, mapTeamToSelectOption, {
        inPortfolio: t(($) => $.adaptivePlanner.teamDropdown.group.inPortfolio),
        notInPortfolio: t(($) => $.adaptivePlanner.teamDropdown.group.notInPortfolio),
      }),
    [teamsById, loopId, t],
  );

  const { commit, isPending } = useTableCellMutation();

  const isImputed = row.imputedFields?.includes(TIMELINE_ITEM_IMPUTED_FIELD.TEAM) ?? false;

  const selectedOption = React.useMemo(() => {
    if (!row.teamId) return null;
    const team = teamsById[row.teamId];
    return team ? mapTeamToSelectOption(team) : null;
  }, [row.teamId, teamsById]);

  return (
    <Select<TeamSelectOption>
      id={cellId}
      name={cellId}
      cellEdit
      autoFocus
      openMenuOnFocus
      menuPlacement="auto"
      isClearable
      value={selectedOption}
      onChange={(opt) => {
        commit({
          itemId,
          api,
          row,
          data: { teamId: opt ? opt.value : null },
        });
      }}
      options={teamAssignmentOptions}
      formatOptionLabel={(option, { context }) => (
        <OptionWithIcon option={option} showImputedBadge={isImputed && context === 'value'} />
      )}
      getOptionValue={(option) => String(option.value)}
      getOptionLabel={(option) => option.label}
      placeholder={t(($) => $.adaptivePlanner.itemPanel.selectPlaceholders.team)}
      isDisabled={isPending}
      closeMenuOnScroll={(e: Event) =>
        e.target instanceof HTMLElement && !e.target.closest('[class*="-menu"]')
      }
    />
  );
};

const OptionWithIcon = ({
  option,
  showImputedBadge,
}: {
  option: { label: string; icon?: React.ReactNode | null };
  showImputedBadge?: boolean;
}) => (
  <div className="flex items-center gap-2 min-w-0">
    {showImputedBadge && <ImputedBadge />}
    {option.icon}
    <span className="truncate">{option.label}</span>
  </div>
);
